import {fromJS} from 'immutable';
import map from 'lodash/map';
import throttle from 'lodash/throttle';
import {AnyAction, Store} from 'redux';

import {CurrentUserSelector} from 'app/current-user/api';
import {accountSettingsSelector} from 'app/settings/api';
import {TaskDashboardLocationParamsType} from 'app/tasks/types';
import {mergeOverwrittingArrays} from 'app/utils/redux';
import {reportToSentry} from 'app/utils/sentry';

import type {DefaultRootState} from 'react-redux';

const RESTORE_STORED_STATE = 'RESTORE_STORED_STATE';

type SerializedRootState = {
  [key in keyof DefaultRootState]: Record<string, any>;
};

interface RestoreStoredStateActionType {
  type: typeof RESTORE_STORED_STATE;
  data: Partial<SerializedRootState> | undefined;
}

// reducer

export const restoreLocaStorageReducer = (
  state: DefaultRootState,
  action: AnyAction
): DefaultRootState => {
  switch (action.type) {
    case RESTORE_STORED_STATE: {
      if (action.data == null) {
        return state;
      }
      map(action.data, (value, key) => {
        // is it a immutable.js Record instance?
        if (typeof state[key]?.toJS === 'function') {
          state[key] = mergeOverwrittingArrays(state[key], fromJS(value));
        } else {
          state[key] = {...state[key], ...value};
        }
      });
      return {...state};
    }

    default:
      return state;
  }
};

// action to restore state

export const loadStatefromLocalStorage = (
  userId: number,
  accountId: number
): RestoreStoredStateActionType => {
  if (userId == null || accountId == null) {
    throw Error('User data is required to load state from localStorage');
  }

  let persistedState = loadState(userId, accountId);

  // since it's hard to predict the localstorage value let's ignore it
  // if some error happens
  if (persistedState) {
    const guaranteedPersistedState = persistedState;
    persistedState = Object.keys(persistedState).reduce((acc, key) => {
      try {
        acc[key] = guaranteedPersistedState[key];
        return acc;
      } catch (err) {
        reportToSentry(
          err,
          {
            badKey: key,
          },
          true
        );
      }
    }, {});
  }

  return {
    type: RESTORE_STORED_STATE,
    data: persistedState,
  };
};

// func to save state to localStorage

export const throttledSaveStateToLocalStorage = throttle(
  (store: Store<DefaultRootState>) => {
    // save store state slices to localStorage on changes
    const state = store.getState();
    // @ts-expect-error - we cannot use a hook here, must use buggy selector
    const accountId = accountSettingsSelector(state).data?.account_id;
    const userId = CurrentUserSelector(state).data?.id;

    // we need the user id to save the user settings
    if (!userId || !accountId) {
      return;
    }

    saveState(userId, accountId, {
      proceduresPage: {
        dashboardSidebar: state.proceduresPage.get('dashboardSidebar'),
        closedSideDiagrams: state.proceduresPage.get('closedSideDiagrams'),
        procedureAdvancedMode: state.proceduresPage.get(
          'procedureAdvancedMode'
        ),
        dashboard: {
          currentFilters: state.proceduresPage.getIn([
            'dashboard',
            'currentFilters',
          ]),
          activeRelations: state.proceduresPage.getIn([
            'dashboard',
            'activeRelations',
          ]),
        },
      },
      tasks: {
        dashboard: {
          allTasksFilters: getLastValidTaskFilter(
            userId,
            accountId,
            state.tasks.dashboard.allTasksFilters
          ),
          lastUsed: state.tasks.dashboard.lastUsed,
          mineCollapsed: state.tasks.dashboard.mineCollapsed,
        },
      },
      settings: {
        widgetIsOpen: state.settings.widgetIsOpen,
      },
      notifications: {
        hideBanner: state.notifications.get('hideBanner'),
        dashboard: {
          currentFilters: state.notifications.getIn([
            'dashboard',
            'currentFilters',
          ]),
        },
      },
      notifly: {
        filters: state.notifly.filters,
      },
      comments: {
        isHidingDeletedComments: state.comments.isHidingDeletedComments,
      },
      sweetai: {
        settings: state.sweetai.settings,
      },
      tags: {
        filters: state.tags.filters,
      },
      processArea: {
        dashboard: {
          currentFilters: state.processArea.getIn([
            'dashboard',
            'currentFilters',
          ]),
          activeRelations: state.processArea.getIn([
            'dashboard',
            'activeRelations',
          ]),
        },
      },
      policies: {
        filters: state.policies.filters,
        relations: state.policies.relations,
      },
      toasts: {
        hiddenWarnings: state.toasts.hiddenWarnings,
      },
      teamsArea: {
        filters: state.teamsArea.get('filters'),
      },
      folders: {
        isHidingStackedDashboardFolders:
          state.folders.isHidingStackedDashboardFolders,
      },
      documents: {
        dashboard: {
          currentFilters: state.documents.getIn([
            'dashboard',
            'currentFilters',
          ]),
        },
      },
    });
  },
  2000
);

// auxiliary

const getOldKey = (userId: number) => `userid-${userId}-settings-state`;

export const getKey = (userId: number, accountId: number): string =>
  `userid-${userId}-accountid-${accountId}-settings-state`;

const getLastValidTaskFilter = (
  userId: number,
  accountId: number,
  filters: TaskDashboardLocationParamsType
) =>
  filters.template_id == null
    ? filters
    : loadState(userId, accountId)?.tasks?.dashboard.allTasksFilters ?? {};

export function loadState(
  userId: number,
  accountId: number
): Partial<SerializedRootState> | undefined {
  /* first try to get data from the old key, without accountId */
  try {
    const oldKey = getOldKey(userId);
    const serializedState = localStorage.getItem(oldKey);

    if (serializedState != null) {
      /* remove old key, the app will save state in the new key */
      localStorage.removeItem(oldKey);
      return JSON.parse(serializedState);
    }
  } catch (err) {
    // ok so no luck here, lets try to use the new key
  }

  try {
    const serializedState = localStorage.getItem(getKey(userId, accountId));
    if (serializedState == null) {
      return undefined;
    }
    return JSON.parse(serializedState);
  } catch (err) {
    return undefined;
  }
}

export function saveState(
  userId: number,
  accountId: number,
  state: Partial<SerializedRootState>
): void {
  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem(getKey(userId, accountId), serializedState);
  } catch (err) {
    // localStorage not supported
  }
}
